"use client";
import { useRef, useState, type ReactNode } from "react";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { api, ApiError, reasonText } from "../../lib/api";
import { Banner, Button, Field, Input, PageHeader } from ".";
import { useUnsavedWork } from "./unsaved-work";

/** These legacy account/grant commands do not accept idempotency keys. Never automatically replay an uncertain write. */
export function AccessAction({
  title,
  eyebrow = "账号与授权 · 办理",
  description,
  dirty,
  disabled = false,
  danger = false,
  label,
  children,
  submit,
  inspect,
  onSaved,
  onCancel
}: {
  title: string;
  eyebrow?: string;
  description: string;
  dirty: boolean;
  disabled?: boolean;
  danger?: boolean;
  label: string;
  children: ReactNode;
  submit: () => Promise<unknown>;
  inspect?: () => Promise<string[]>;
  onSaved: (result: unknown) => void;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [reauth, setReauth] = useState(false),
    [password, setPassword] = useState("");
  const [uncertain, setUncertain] = useState(false),
    [snapshot, setSnapshot] = useState<string[] | null>(null),
    [retryChecked, setRetryChecked] = useState(false),
    [checking, setChecking] = useState(false);
  const lock = useRef(false),
    guard = useUnsavedWork(dirty || !!password, busy || checking);
  return (
    <div className="record-editor access-editor">
      <Button variant="ghost" className="record-back" onClick={() => guard.requestLeave(onCancel)}>
        <ArrowLeft size={16} />
        返回
      </Button>
      <PageHeader eyebrow={eyebrow} title={title} description={description} />
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (lock.current || disabled || (uncertain && !retryChecked)) return;
          lock.current = true;
          setBusy(true);
          setError("");
          let sending = false;
          try {
            if (reauth) {
              await api("/api/v1/auth/reauth", { method: "POST", body: { password } });
              setPassword("");
              setReauth(false);
            }
            sending = true;
            const result = await submit();
            setPassword("");
            onSaved(result);
          } catch (caught) {
            setError(reasonText(caught));
            if (caught instanceof ApiError && caught.reasonCode === "REAUTH_REQUIRED") {
              setReauth(true);
              setPassword("");
            } else if (sending && (!(caught instanceof ApiError) || caught.status >= 500)) {
              setUncertain(true);
              setSnapshot(null);
              setRetryChecked(false);
            }
          } finally {
            lock.current = false;
            setBusy(false);
          }
        }}
      >
        <fieldset className="record-fields" disabled={busy || checking || uncertain}>
          {children}
        </fieldset>
        <fieldset className="record-fields" disabled={busy || checking}>
          {reauth && (
            <section className="access-reauth">
              <h2>
                <ShieldCheck size={18} />
                确认是本人操作
              </h2>
              <p>填写你当前账号的口令，验证通过后提交上述办理。填写的材料会保留。</p>
              <Field label="验证口令">
                <Input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
            </section>
          )}
        </fieldset>
        {error && (
          <div className="access-save-error">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}
        {uncertain && (
          <section className="access-uncertain">
            <h2>办理结果尚未确认</h2>
            <p>没有收到完整的办理回执。请先读取最新记录，核对是否已经完成。</p>
            {inspect ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={checking || busy}
                  onClick={async () => {
                    setChecking(true);
                    setRetryChecked(false);
                    try {
                      setSnapshot(await inspect());
                    } catch (e) {
                      setError(reasonText(e));
                      setSnapshot(null);
                    } finally {
                      setChecking(false);
                    }
                  }}
                >
                  {checking ? "正在核对…" : "读取最新记录"}
                </Button>
                {snapshot && (
                  <>
                    <ul>
                      {snapshot.length ? (
                        snapshot.map((s, i) => <li key={i}>{s}</li>)
                      ) : (
                        <li>本次查询没有匹配记录。</li>
                      )}
                    </ul>
                    <label className="record-check">
                      <input
                        type="checkbox"
                        checked={retryChecked}
                        onChange={(e) => setRetryChecked(e.target.checked)}
                      />
                      我已核对，本次办理尚未完成，继续提交
                    </label>
                  </>
                )}
              </>
            ) : (
              <p>请重新登录，核对口令修改结果。</p>
            )}
          </section>
        )}
        <div className="record-editor-footer">
          <span>{busy ? "正在提交，请稍候" : "办理结果保留在原记录中"}</span>
          <div className="workspace-actions">
            <Button
              type="button"
              variant="outline"
              disabled={busy || checking}
              onClick={() => guard.requestLeave(onCancel)}
            >
              取消
            </Button>
            <Button
              type="submit"
              variant={danger ? "destructive" : "default"}
              disabled={busy || checking || disabled || (uncertain && !retryChecked)}
            >
              {busy ? "正在提交…" : reauth ? "验证并提交" : label}
            </Button>
          </div>
        </div>
      </form>
      {guard.dialog}
    </div>
  );
}
